import {
  Box,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { IWashRequest, IWashReview, IPartnerReview, WashStatus } from "../../types/wash.types";
import { hasCustomerReview, hasPartnerReview, isRatingSkipped } from "./reviewUtils";

export type StatusFilter = WashStatus | "all";

export const STATUS_OPTIONS: { value: StatusFilter; labelKey: string }[] = [
  { value: "all", labelKey: "status.all" },
  { value: "pending", labelKey: "washDetail.statusPending" },
  { value: "accepted", labelKey: "washDetail.statusAccepted" },
  { value: "in_progress", labelKey: "washDetail.statusInProgress" },
  { value: "completed", labelKey: "washDetail.statusCompleted" },
  { value: "cancelled", labelKey: "washDetail.statusCancelled" },
  { value: "not_arrive", labelKey: "washDetail.statusNotArrived" },
];

export const STATUS_LABEL_KEYS: Record<string, string> = {
  pending: "washDetail.statusPending",
  accepted: "washDetail.statusAccepted",
  in_progress: "washDetail.statusInProgress",
  completed: "washDetail.statusCompleted",
  cancelled: "washDetail.statusCancelled",
  not_arrive: "washDetail.statusNotArrived",
};

export const SERVICE_TYPE_LABEL_KEYS: Record<string, string> = {
  inner: "vehicle.serviceInner",
  outer: "vehicle.serviceOuter",
  both: "vehicle.serviceBoth",
};

export function getStatusColor(
  status: string
): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" {
  switch (status) {
    case "pending":
      return "warning";
    case "accepted":
      return "info";
    case "in_progress":
      return "primary";
    case "completed":
      return "success";
    case "cancelled":
    case "not_arrive":
      return "error";
    default:
      return "default";
  }
}

export function formatDuration(
  t: (key: string, opts?: Record<string, unknown>) => string,
  minutes?: number
): string {
  if (minutes == null) return "—";
  if (minutes < 60) return t("washingHistory.durationMinutes", { count: minutes });
  return t("washingHistory.durationHours", {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  });
}

/**
 * Customer review cell. Never uses a bare truthiness check on `washReview` (F-01) —
 * the backend always serializes an unreviewed wash's review as `{ tags: [] }`,
 * so presence is decided by `hasCustomerReview` (createdAt), and a rating of 0
 * is rendered as "Skipped", never "0 / 5".
 */
export function ReviewSummary({ wash }: { wash: { washReview?: IWashReview } }) {
  const { t } = useTranslation();

  if (!hasCustomerReview(wash)) {
    return <>—</>;
  }

  const review = wash.washReview as IWashReview;
  const ratingLabel = isRatingSkipped(review)
    ? t("washingHistory.ratingSkipped")
    : t("washingHistory.ratingOutOf", { rating: review.rating });

  return (
    <Box>
      <Typography variant="body2">{ratingLabel}</Typography>
      {review.message && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          title={review.message}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {review.message}
        </Typography>
      )}
      {!!review.tags?.length && (
        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
          {review.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      )}
    </Box>
  );
}

/** Partner review cell — mirrors ReviewSummary's non-bare-truthiness rule. */
export function PartnerReviewSummary({ wash }: { wash: { partnerReview?: IPartnerReview } }) {
  const { t } = useTranslation();

  if (!hasPartnerReview(wash)) {
    return <>—</>;
  }

  const review = wash.partnerReview as IPartnerReview;

  return (
    <Box>
      {review.rating != null && (
        <Typography variant="body2">
          {t("washingHistory.ratingOutOf", { rating: review.rating })}
        </Typography>
      )}
      {review.message && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          title={review.message}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {review.message}
        </Typography>
      )}
    </Box>
  );
}

export interface WashHistoryTableProps {
  washes: IWashRequest[];
  loading: boolean;
  error: string | null;
  onDismissError?: () => void;
  title: string;
  emptyMessage: string;
  showCarWash?: boolean;
  showCustomer?: boolean;
  showEmployee?: boolean;
}

const WashHistoryTable = ({
  washes,
  loading,
  error,
  onDismissError,
  title,
  emptyMessage,
  showCarWash = false,
  showCustomer = true,
  showEmployee = false,
}: WashHistoryTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const filtered =
    statusFilter === "all" ? washes : washes.filter((w) => w.status === statusFilter);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">{title}</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("common.status")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("common.status")}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper variant="outlined">
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              {statusFilter === "all"
                ? emptyMessage
                : t("washingHistory.emptyFiltered", {
                    status: t(STATUS_LABEL_KEYS[statusFilter] ?? "common.unknown"),
                  })}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("common.date")}</TableCell>
                {showCarWash && <TableCell>{t("washingHistory.colCarWash")}</TableCell>}
                {showCustomer && <TableCell>{t("washingHistory.colCustomer")}</TableCell>}
                {showEmployee && <TableCell>{t("washingHistory.colEmployee")}</TableCell>}
                <TableCell>{t("manageCarWashes.vehicle")}</TableCell>
                <TableCell>{t("manageCarWashes.service")}</TableCell>
                <TableCell>{t("manageCarWashes.class")}</TableCell>
                <TableCell align="right">{t("common.price")}</TableCell>
                <TableCell align="center">{t("common.status")}</TableCell>
                <TableCell>{t("washingHistory.colCustomerReview")}</TableCell>
                <TableCell>{t("washingHistory.colPartnerReview")}</TableCell>
                <TableCell>{t("washingHistory.colDuration")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((wash) => (
                <TableRow
                  key={wash._id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/dashboard/washes/${wash._id}`, {
                      state: { from: location.pathname },
                    })
                  }
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(wash.createdAt).toLocaleString()}
                  </TableCell>
                  {showCarWash && (
                    <TableCell>{wash.carWash?.legalName ?? "—"}</TableCell>
                  )}
                  {showCustomer && (
                    <TableCell>
                      {wash.customer ? (
                        <>
                          {wash.customer.firstName} {wash.customer.lastName}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {wash.customer.country_code} {wash.customer.phone}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {t("washingHistory.deletedUser")}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {showEmployee && (
                    <TableCell>
                      {wash.respondedBy
                        ? `${wash.respondedBy.firstName} ${wash.respondedBy.lastName}`
                        : "—"}
                    </TableCell>
                  )}
                  <TableCell>
                    {wash.vehicleDetails?.brand} {wash.vehicleDetails?.model}
                    {wash.vehicleDetails?.plateNumber && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {wash.vehicleDetails.plateNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {SERVICE_TYPE_LABEL_KEYS[wash.serviceType]
                      ? t(SERVICE_TYPE_LABEL_KEYS[wash.serviceType])
                      : wash.serviceType}
                  </TableCell>
                  <TableCell>
                    {wash.washingClass ? t(`washClass.${wash.washingClass}`) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <strong>{wash.price} ₼</strong>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        STATUS_LABEL_KEYS[wash.status] ? t(STATUS_LABEL_KEYS[wash.status]) : wash.status
                      }
                      color={getStatusColor(wash.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <ReviewSummary wash={wash} />
                  </TableCell>
                  <TableCell>
                    <PartnerReviewSummary wash={wash} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDuration(t, wash.durationMinutes)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {t("washingHistory.recordCount", {
            shown: filtered.length,
            total: washes.length,
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default WashHistoryTable;
